import { useCallback, useMemo, useEffect, useRef } from "react"
import { signal } from "@preact/signals"
import { toast } from "react-toastify"
import _ from "lodash"

// 定义错误类型枚举
type ErrType =
    | "internal" // 内部错误
    | "not_found" // 资源未找到
    | "validation" // 验证错误
    | "authentication" // 认证错误
    | "authorization" // 授权错误
    | "rate_limit" // 速率限制
    | "network" // 网络错误
    | "timeout" // 超时错误
    | "concurrency" // 并发错误

interface ErrorDetails {
    code: number
    message: string
    type: ErrType
}

type QueryType = TransactionApiTypes.Fetcher.Request.Params
type API = (params: QueryType) => Promise<TransactionApiTypes.Fetcher.Response.Result>
type Record = TransactionApiTypes.Fetcher.Response.Record

interface UseInfiniteFetchWithCursorProps {
    list: Record[] // 当前数据列表
    loading: boolean // 加载状态
    fetchData: (queryParam?: QueryType) => Promise<void> // 获取数据方法
    hasMore: boolean // 是否还有更多数据
    requestParams?: QueryType // 当前请求参数
    resetData: () => void // 重置数据方法
    error?: ErrorDetails // 错误信息
}

// 创建全局信号
const createSignals = () => {
    const listSignal = signal<Record[]>([])
    const loadingSignal = signal(false)
    const hasMoreSignal = signal(true)
    const requestParamsSignal = signal<QueryType | undefined>(undefined)
    const errorSignal = signal<ErrorDetails | undefined>(undefined)
    const currentPageSignal = signal(1)
    const requestIdSignal = signal(0)

    return {
        listSignal,
        loadingSignal,
        hasMoreSignal,
        requestParamsSignal,
        errorSignal,
        currentPageSignal,
        requestIdSignal,
    }
}

// 全局信号实例
const globalSignals = createSignals()

/**
 * 基于游标的分页数据获取Hook
 * @param api 数据获取API函数
 * @param initialParams 初始查询参数
 * @returns 包含数据列表、加载状态等信息的对象
 */
export default function useTransactionList(
    api: API,
    initialParams: QueryType,
): UseInfiniteFetchWithCursorProps {
    // 稳定化initialParams
    const stableInitialParams = useMemo(() => initialParams, [initialParams])

    // 初始化信号值
    useEffect(() => {
        globalSignals.requestParamsSignal.value = stableInitialParams

        // 组件卸载时重置数据
        return () => {
            resetData()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /**
     * 获取数据方法
     */
    const fetchData = useCallback(async () => {
        if (globalSignals.loadingSignal.value) return

        // 生成唯一请求ID
        const currentRequestId = Date.now()
        globalSignals.requestIdSignal.value = currentRequestId

        globalSignals.loadingSignal.value = true
        try {
            const params = {
                page: globalSignals.currentPageSignal.value,
                page_size: 10,
                ...globalSignals.requestParamsSignal.value,
            }

            const response = await api(params)

            // 只处理短时间内最新请求的响应
            if (globalSignals.requestIdSignal.value !== currentRequestId) return

            const list = response?.list || []
            const _total = response?.total || 0

            // 计算是否还有更多数据
            const _page = params.page
            const _pageSize = params.page_size

            const hasMore = _page * _pageSize < _total

            globalSignals.listSignal.value = [...globalSignals.listSignal.value, ...list]
            globalSignals.hasMoreSignal.value = hasMore || false
            globalSignals.currentPageSignal.value = hasMore ? params.page + 1 : params.page
        } catch (error) {
            console.error("Error fetching data:", error)
            globalSignals.errorSignal.value = error as ErrorDetails
            toast.error("Error fetching data.")
        } finally {
            globalSignals.loadingSignal.value = false
        }
    }, [api])

    const resetData = useCallback(() => {
        globalSignals.listSignal.value = []
        globalSignals.loadingSignal.value = false
        globalSignals.hasMoreSignal.value = true
        globalSignals.requestParamsSignal.value = stableInitialParams
        globalSignals.errorSignal.value = undefined
        globalSignals.currentPageSignal.value = 1
    }, [stableInitialParams])

    // 使用深比较判断initialParams是否真的变化
    const initialParamsRef = useRef(stableInitialParams)
    useEffect(() => {
        if (!_.isEqual(initialParamsRef.current, stableInitialParams)) {
            initialParamsRef.current = stableInitialParams
            resetData()
        }
    }, [stableInitialParams, resetData])

    // 当请求参数变化时获取数据
    useEffect(() => {
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalSignals.requestParamsSignal.value, fetchData])

    // 返回Hook提供的所有状态和方法
    return {
        list: globalSignals.listSignal.value,
        loading: globalSignals.loadingSignal.value,
        fetchData,
        hasMore: globalSignals.hasMoreSignal.value,
        requestParams: globalSignals.requestParamsSignal.value,
        resetData,
        error: globalSignals.errorSignal.value,
    }
}
